import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import MuiTypography from "@mui/material/Typography";
import FormLabel from "@mui/material/FormLabel";
import MuiTextField from "@mui/material/TextField";
import { cleanProps } from "../internal/cleanProps";
import { fieldErrorSx, fieldHelpSx, fieldLabelSx } from "../internal/field-styles";
import { tokens } from "../tokens";
import type { TextFieldProps } from "../types";

/**
 * Containment note (types.ts): upstream `TextField<Variant>` is fixed to
 * `'outlined'` permanently — this component never passes a `variant` prop
 * through to the caller and no `Variant` parameter exists on the adapter's
 * type.
 *
 * `labelPosition="above"` (default, ADR-20260917-gh2a / `frp-textfield-label.js`)
 * renders the label as its own element above the control and suppresses
 * Material UI's own floating label; `"inline"` uses Material UI's standard
 * label placement. Help text is stated before entry, above the control,
 * per the handoff's "formats and limits must be stated before entry" rule.
 */
export function TextField({
  id,
  label,
  labelPosition = "above",
  value,
  onChange,
  help,
  labelAction,
  helpPanel,
  error,
  errorText,
  required,
  disabled,
  multiline,
  rows,
  type = "text",
  fullWidth,
  name,
  autoComplete,
  testId,
  figure,
}: Readonly<TextFieldProps>) {
  const helpId = help !== undefined || helpPanel !== undefined ? `${id}-help` : undefined;
  const errorId = error === true && errorText !== undefined ? `${id}-error` : undefined;
  const describedBy = [helpId, errorId].filter((value): value is string => value !== undefined).join(" ") || undefined;

  const field = (
    <MuiTextField
      id={id}
      variant="outlined"
      value={value}
      onChange={(event) => {
        onChange(event.target.value, event);
      }}
      type={type}
      {...cleanProps({
        label: labelPosition === "inline" ? label : undefined,
        hiddenLabel: labelPosition === "above" ? true : undefined,
        error,
        required,
        disabled,
        multiline,
        rows,
        fullWidth,
        name,
        autoComplete,
        "data-testid": testId,
      })}
      slotProps={{ htmlInput: cleanProps({ "aria-describedby": describedBy }) }}
    />
  );

  if (labelPosition === "inline") {
    return (
      <FormControl {...cleanProps({ fullWidth, error, required, disabled })}>
        {help !== undefined ? <FormHelperText id={helpId} sx={{ margin: 0 }}>{help}</FormHelperText> : null}
        {field}
        {error === true && errorText !== undefined ? (
          <FormHelperText id={errorId} error>
            {errorText}
          </FormHelperText>
        ) : null}
      </FormControl>
    );
  }

  if (figure === true) {
    // H2's figure question: the question in bold above, the field 260px wide with its own label
    // kept for screen readers only (H2's ds-textfield departure).
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: `${tokens.spacing.xs}px` }}>
        <Box component="span" aria-hidden="true" sx={{ fontSize: 17, fontWeight: tokens.typography.weights.bold, color: tokens.colour.neutral.ink }}>
          {label}
        </Box>
        <Box sx={{ maxWidth: 260 }}>
          <FormControl fullWidth {...cleanProps({ error, required, disabled })}>
            <FormLabel htmlFor={id} sx={{ ...fieldLabelSx, ...visuallyHidden }}>
              {label}
            </FormLabel>
            {help !== undefined ? (
              <MuiTypography id={helpId} component="p" variant="caption" sx={fieldHelpSx}>
                {help}
              </MuiTypography>
            ) : null}
            {field}
            {error === true && errorText !== undefined ? (
              <MuiTypography id={errorId} component="p" variant="caption" sx={fieldErrorSx}>
                {errorText}
              </MuiTypography>
            ) : null}
          </FormControl>
        </Box>
      </Box>
    );
  }

  // ADR-20261102-h2fd: the H2 design system's FormField -- label in ink and bold, staying ink in
  // error; help in inkMuted 8px above the control; the error message bold in errorText 4px below.
  return (
    <FormControl sx={{ mb: `${tokens.spacing.lg}px` }} {...cleanProps({ fullWidth, error, required, disabled })}>
      <FormLabel htmlFor={id} sx={fieldLabelSx}>
        {label}
        {labelAction}
      </FormLabel>
      {helpPanel !== undefined ? (
        <Box id={helpId} sx={fieldHelpSx}>
          {helpPanel}
        </Box>
      ) : null}
      {help !== undefined && helpPanel === undefined ? (
        <MuiTypography id={helpId} component="p" variant="caption" sx={fieldHelpSx}>
          {help}
        </MuiTypography>
      ) : null}
      {field}
      {error === true && errorText !== undefined ? (
        <MuiTypography id={errorId} component="p" variant="caption" sx={fieldErrorSx}>
          {errorText}
        </MuiTypography>
      ) : null}
    </FormControl>
  );
}

const visuallyHidden = {
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  overflow: "hidden",
  clipPath: "inset(50%)",
  whiteSpace: "nowrap",
} as const;
